import ky, { type KyInstance, HTTPError } from "ky";
import { apiConfig } from "@/shared/config/api";
import type { ApiError } from "./types";
import { getAccessToken } from "@/shared/lib/auth-token";

export class ApiClientError extends Error {
  status: number;
  statusText: string;
  body?: ApiError;

  constructor(
    status: number,
    statusText: string,
    body?: ApiError,
    message?: string
  ) {
    super(message || body?.message || statusText);
    this.name = "ApiClientError";
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

const createErrorHandler = async (error: HTTPError): Promise<HTTPError> => {
  let errorBody: ApiError | undefined;

  try {
    errorBody = (await error.response.json()) as ApiError;
  } catch {
    errorBody = {
      message: error.response.statusText || "An error occurred",
      statusCode: error.response.status,
    };
  }

  throw new ApiClientError(
    error.response.status,
    error.response.statusText,
    errorBody,
    errorBody?.detail || errorBody?.message
  );
};

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

export const createApiClient = (): KyInstance => {
  const client = ky.create({
    prefixUrl: apiConfig.baseUrl,
    timeout: apiConfig.timeout,
    headers: apiConfig.headers,
    retry: {
      limit: 1,
      methods: ["get", "put", "head", "delete", "options", "trace", "post"],
      statusCodes: [401],
    },
    hooks: {
      beforeError: [createErrorHandler],
      beforeRequest: [
        async (request) => {
          const token = await getAccessToken();
          if (import.meta.env.DEV) {
            console.log(token);
          }
          if (token) {
            request.headers.set("Authorization", `Bearer ${token}`);
          }
        },
      ],
      beforeRetry: [
        async ({ request, error }) => {
          if (error instanceof HTTPError && error.response.status === 401) {
            if (isRefreshing && refreshPromise) {
              const newToken = await refreshPromise;
              if (newToken) {
                request.headers.set("Authorization", `Bearer ${newToken}`);
                return;
              }
              throw error;
            } else {
              isRefreshing = true;
              refreshPromise = getAccessToken(undefined, true);

              try {
                const newToken = await refreshPromise;
                if (newToken) {
                  request.headers.set("Authorization", `Bearer ${newToken}`);
                  return;
                } else {
                  console.error(
                    "[Token Refresh] Failed to refresh token after 401"
                  );
                  throw error;
                }
              } catch (refreshError) {
                console.error(
                  "[Token Refresh] Token refresh error:",
                  refreshError
                );
                throw error;
              } finally {
                isRefreshing = false;
                refreshPromise = null;
              }
            }
          }
        },
      ],
      afterResponse: [
        async (_request, _options, response) => {
          return response;
        },
      ],
    },
  });

  return client;
};

export const apiClient = createApiClient();

export type { ApiError } from "./types";
